/**
 * «¿Prefieres español?» — ofrecido, nunca impuesto.
 *
 * ── Qué problema resuelve ───────────────────────────────────────────────────
 * Alguien que llega desde México con el navegador en inglés ve el panel en
 * inglés, que es correcto —`Accept-Language` es lo que esa persona configuró—
 * pero puede no ser lo que quiere aquí. Y al revés: alguien de paso por España
 * con el navegador en español no quiere que le cambiemos nada.
 *
 * ── Por qué se pregunta en vez de decidir ───────────────────────────────────
 * Porque el país NO es el idioma. Un mexicano en Berlín no lee alemán; un
 * estadounidense en Ciudad de México no lee español. Cambiar el idioma solo,
 * por dónde está el router de alguien, es el fallo clásico de esta función: se
 * siente como que la aplicación sabe más que tú y se equivoca.
 *
 * Así que el orden es: lo que la persona eligió manda siempre; si no eligió,
 * manda su navegador; y el país sólo sirve para OFRECER una alternativa, una
 * vez, sin tocar nada hasta que la pulse.
 *
 * ── Cuándo NO aparece ───────────────────────────────────────────────────────
 * - Si ya eligió idioma alguna vez. Preguntárselo otra vez es no escuchar.
 * - Si el país sugiere el idioma que ya está puesto. No hay nada que ofrecer.
 * - Si el país no está en nuestra lista, o la petición falla. Callar es mejor
 *   que adivinar.
 * - Si ya lo descartó en esta sesión.
 */
import { useEffect, useState } from 'react'
import { IconLanguage, IconX } from '@tabler/icons-react'
import { LANGUAGES, hasChosenLanguage, isLanguage, type Language } from '@webbuilder/i18n'
import { useI18n } from '../lib/i18n'
import { WORKER_URL } from '../lib/api'

/** Sólo dentro de esta pestaña: no es una preferencia, es un «ahora no». */
let descartadoEnEstaSesion = false

export function LanguageSuggestion() {
  const { t, language, setLanguage } = useI18n()
  const [sugerido, setSugerido] = useState<Language | null>(null)

  useEffect(() => {
    if (descartadoEnEstaSesion || hasChosenLanguage()) return
    let vivo = true
    void fetch(`${WORKER_URL}/geo`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { data?: { language?: unknown } } | null) => {
        if (!vivo) return
        const propuesto = body?.data?.language
        /* Sólo si es un idioma que hablamos Y distinto del puesto. */
        if (!isLanguage(propuesto) || propuesto === language) return
        if (!LANGUAGES.includes(propuesto)) return
        setSugerido(propuesto)
      })
      .catch((error: unknown) => {
        /* Sin país no se ofrece nada, y ya está: esto es una comodidad, no una
           función de la que dependa nadie. Al registro igualmente, porque un
           fallo constante aquí querría decir que el endpoint está roto. */
        console.warn('[geo] no se pudo leer el país:', error)
      })
    return () => { vivo = false }
  }, [language])

  if (!sugerido) return null

  function descartar() {
    descartadoEnEstaSesion = true
    setSugerido(null)
  }

  return (
    <div className="lang-suggestion" role="status">
      <IconLanguage size={15} aria-hidden="true" />
      <span>{t('language.suggest', { language: t(`language.${sugerido}`) })}</span>
      <button type="button" className="lang-suggestion-accept" onClick={() => { setLanguage(sugerido); descartar() }}>
        {t('language.suggestSwitch')}
      </button>
      <button type="button" className="lang-suggestion-close" aria-label={t('common.close')} onClick={descartar}>
        <IconX size={14} aria-hidden="true" />
      </button>
    </div>
  )
}
